/**
 * useChatUIState Hook
 * ===================
 * 
 * Manages chat UI state:
 * - Composer text
 * - Menu open/close
 * - Reaction picker state
 * - Loading states
 */
import { useState, useCallback, useRef } from 'react';

export function useChatUIState() {
  // Composer state
  const [newMessage, setNewMessage] = useState('');
  
  // Menu state
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  
  // Reaction picker state
  const [reactionUI, setReactionUI] = useState({
    openFor: null,  // message ID
    top: 0,
    left: 0,
    isOwn: false,
  });
  const reactionAnchorRef = useRef(null);
  
  // Video call state
  const [videoInviteSent, setVideoInviteSent] = useState(false);
  const [isStartingVideo, setIsStartingVideo] = useState(false);
  
  // Modal states
  const [showReport, setShowReport] = useState(false);
  const [showVideoLimitModal, setShowVideoLimitModal] = useState(false);
  const [showInnerWorkModal, setShowInnerWorkModal] = useState(false);
  
  // Inner work nudge
  const [innerWorkPrompt, setInnerWorkPrompt] = useState(null);
  const [showInnerNudge, setShowInnerNudge] = useState(false);
  
  // Private gallery
  const [isGrantingPrivateAccess, setIsGrantingPrivateAccess] = useState(false);
  const [hasPrivateGalleryAccess, setHasPrivateGalleryAccess] = useState(false);
  
  // Toggle menu
  const toggleMenu = useCallback(() => {
    setShowMoreMenu(prev => !prev);
  }, []);
  
  const closeMenu = useCallback(() => {
    setShowMoreMenu(false);
  }, []);
  
  // Reaction picker handlers
  const openReactionPicker = useCallback((message, event) => {
    if (!event) return;
    
    const rect = event.currentTarget.getBoundingClientRect();
    const isOwn = message.isOwnMessage;
    
    // Position picker above the message
    let top = rect.top - 50;
    let left = isOwn ? rect.right - 340 : rect.left;
    
    // Ensure within viewport
    if (left < 8) left = 8;
    if (left + 340 > window.innerWidth) left = window.innerWidth - 348;
    if (top < 8) top = rect.bottom + 8;
    
    setReactionUI({
      openFor: message.id,
      top,
      left,
      isOwn,
    });
    reactionAnchorRef.current = event.currentTarget;
  }, []);
  
  const closeReactionPicker = useCallback(() => {
    setReactionUI({
      openFor: null,
      top: 0,
      left: 0,
      isOwn: false,
    });
  }, []);
  
  // Clear message input
  const clearMessage = useCallback(() => {
    setNewMessage('');
  }, []);
  
  return {
    // Composer
    newMessage,
    setNewMessage,
    clearMessage,
    
    // Menu
    showMoreMenu,
    toggleMenu,
    closeMenu,
    
    // Reaction picker
    reactionUI,
    reactionAnchorRef,
    openReactionPicker,
    closeReactionPicker,
    
    // Video
    videoInviteSent,
    setVideoInviteSent,
    isStartingVideo,
    setIsStartingVideo,
    
    // Modals
    showReport,
    setShowReport,
    showVideoLimitModal,
    setShowVideoLimitModal,
    showInnerWorkModal,
    setShowInnerWorkModal,
    
    // Inner work
    innerWorkPrompt,
    setInnerWorkPrompt,
    showInnerNudge,
    setShowInnerNudge,
    
    // Private gallery
    isGrantingPrivateAccess,
    setIsGrantingPrivateAccess,
    hasPrivateGalleryAccess,
    setHasPrivateGalleryAccess,
  };
}

export default useChatUIState;
